import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Card from "../../ui/Card/Card";
import Button from "../../ui/Button/Button";
import IconButton from "../../ui/IconButton/IconButton";
import { Book, Close, ThumbsDown, ThumbsUp } from "../../ui/Icon/Icon";
import Textarea from "../../ui/Textarea/Textarea";
import TileButton from "../../ui/TileButton/TileButton";
import styles from "./ArticleFeedbackFooter.module.scss";
import { graphql } from "../../__generated__/gql";
import { useMutation } from "urql";
import { Quality } from "../../__generated__/gql/graphql";

const AddArticleFeedbackQuality = graphql(`
  mutation addArticleFeedbackQuality($id: String!, $quality: Quality!) {
    addArticleFeedback(input: { id: $id, quality: $quality }) {
      id
    }
  }
`);

const AddArticleFeedbackComment = graphql(`
  mutation addArticleFeedbackComment($id: String!, $comment: String!) {
    addArticleFeedback(input: { id: $id, comment: $comment }) {
      id
    }
  }
`);

function QualityStep({
  id,
  onSubmit,
}: {
  id: string;

  onSubmit: (quality: Quality) => void;
}) {
  const [addArticleFeedbackQualityResponse, addArticleFeedbackQuality] =
    useMutation(AddArticleFeedbackQuality);

  const handleSubmit = (quality: Quality) => {
    // Don't wait for the response to update the UI
    void addArticleFeedbackQuality({ id, quality });

    onSubmit(quality);
  };

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <span>How is the quality of this article?</span>
        <div className={styles.buttons}>
          <Button
            variant="outline"
            size="medium"
            loading={addArticleFeedbackQualityResponse.fetching}
            onClick={() => handleSubmit(Quality.Bad)}
          >
            <ThumbsDown size="medium" />
          </Button>
          <Button
            variant="outline"
            size="medium"
            loading={addArticleFeedbackQualityResponse.fetching}
            onClick={() => handleSubmit(Quality.Good)}
          >
            <ThumbsUp size="medium" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
function CommentStep({
  id,
  setStep,
  dismiss,
  quality,
}: {
  id: string;
  setStep: (step: "thanks") => void;
  dismiss: () => void;
  quality: Quality;
}) {
  const [, addArticleFeedbackComment] = useMutation(AddArticleFeedbackComment);
  const ref = useRef<HTMLDivElement>(null);

  const { control, handleSubmit, watch, setFocus } = useForm<{
    comment: string;
  }>();

  const comment = watch("comment");

  const onSubmit = (data: { comment: string }) => {
    // Don't wait for the response to update the UI
    void addArticleFeedbackComment({
      id,
      comment: data.comment,
    });

    setStep("thanks");
  };

  useEffect(() => {
    ref.current?.scrollIntoView({ block: "end" });
    setFocus("comment");
  }, [setFocus]);

  return (
    <Card className={styles.card} ref={ref}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.header}>
          <span>
            {quality === Quality.Bad
              ? "Sorry to hear that! What could I improve?"
              : "Amazing! What do you like about it?"}
          </span>

          <IconButton
            IconComponent={Close}
            aria-label="Dismiss"
            onClick={dismiss}
          />
        </div>
        <div className={styles.body}>
          <Controller
            control={control}
            name="comment"
            render={({ field }) => (
              <Textarea
                {...field}
                placeholder={
                  quality === Quality.Bad
                    ? "What didn't you like?"
                    : "What did you like?"
                }
                rows={3}
                className={styles.textarea}
              />
            )}
          />
          <Button
            variant="primary"
            size="small"
            type="submit"
            disabled={!comment}
            className={styles.submitButton}
          >
            Submit
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ThanksStep({
  dismiss,
  quality,
}: {
  dismiss: () => void;
  quality: Quality;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.scrollIntoView({ block: "end" });
  }, []);

  return (
    <Card className={styles.card} ref={ref}>
      <div className={styles.header}>
        <span>Thanks! We will review your feedback.</span>

        <IconButton
          IconComponent={Close}
          aria-label="Dismiss"
          onClick={dismiss}
        />
      </div>
      <div className={styles.body}>
        <p>
          {quality === Quality.Bad
            ? "In the meantime, check out the guide below to learn how you can produce higher quality articles with Macaw"
            : "In the meantime, check out the guide below to learn how you can produce even higher quality articles with Macaw"}
        </p>
        <TileButton
          IconComponent={Book}
          onClick={() =>
            window.open(
              "https://docs.macawhq.com/en/articles/9912541-fine-tuning-articles-for-improved-quality",
              "_blank",
            )
          }
          title="Learn how to improve generation"
          subtitle="Fine-tuning guide for improved output"
        />
      </div>
    </Card>
  );
}

function InternalArticleFeedbackCard({
  id,
  dismiss,
}: {
  id: string;
  dismiss: () => void;
}) {
  const [step, setStep] = useState<"quality" | "comment" | "thanks">("quality");
  const [quality, setQuality] = useState<Quality>(Quality.Good);

  if (step === "quality") {
    return (
      <QualityStep
        id={id}
        onSubmit={(q) => {
          setQuality(q);
          setStep("comment");
        }}
      />
    );
  }

  if (step === "comment") {
    return (
      <CommentStep
        id={id}
        setStep={setStep}
        dismiss={dismiss}
        quality={quality}
      />
    );
  }

  return <ThanksStep dismiss={dismiss} quality={quality} />;
}

function ArticleFeedbackFooter({
  id,
  isDismissed,
}: {
  id: string;
  isDismissed?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(!isDismissed);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.footer}>
      <InternalArticleFeedbackCard id={id} dismiss={() => setIsOpen(false)} />
    </div>
  );
}

export default ArticleFeedbackFooter;
